import type { ApiClient } from './client';
import type { ReportRange, ScanSummary, ScanVerdict } from './reports';

/**
 * The Reports screen's figures, D12.
 *
 * `GET /v1/reports/heatmap`, read off `veri-plus-backend`:
 * `validation/report.validation.ts` `heatmapQuery` takes the summary range, an
 * optional `verdict` and `precision` 0 to 4 (default 2, a cell of about
 * 1.1km). `scanAggregation.heatmapPipeline` buckets located scans only, sorts
 * cells by total and stops at 5,000. `report.controller.heatmap` answers
 * `{ precision, cells }`. A verdict with no scans in a cell is absent from its
 * `counts`, not zero.
 */

export interface HeatmapCell {
  lat: number;
  lng: number;
  total: number;
  counts: Partial<Record<ScanVerdict, number>>;
}

export interface Heatmap {
  precision: number;
  cells: HeatmapCell[];
}

export function heatmapPath(range: ReportRange): string {
  const query = new URLSearchParams();
  if (range.from) query.set('from', range.from.toISOString());
  if (range.to) query.set('to', range.to.toISOString());
  const qs = query.toString();
  return qs === '' ? '/v1/reports/heatmap' : `/v1/reports/heatmap?${qs}`;
}

/** Every verdict at once: the pills filter locally, so one request serves all three, D12. */
export function getHeatmap(
  api: ApiClient,
  range: ReportRange,
  signal?: AbortSignal,
): Promise<Heatmap> {
  return api.get<Heatmap>(heatmapPath(range), signal ? { signal } : {});
}

/**
 * The board's 760x470 map and the four cities it labels.
 *
 * `x`, `y` are the centres of the circles `PortalReports` draws beside each
 * label, and `labelX`, `labelY` the label's own position, both literal in the
 * board. `lat`, `lng` are where the cities are. The outline is drawn by hand,
 * so these four do not agree on one scale; the projection below is the least
 * squares fit through them, D12.
 */
export const mapView = { width: 760, height: 470 } as const;

export const landmarks = [
  { name: 'Nairobi', lat: -1.286, lng: 36.817, x: 430, y: 250, labelX: 452, labelY: 246 },
  { name: 'Mombasa', lat: -4.043, lng: 39.668, x: 600, y: 370, labelX: 622, labelY: 366 },
  { name: 'Kisumu', lat: -0.092, lng: 34.768, x: 210, y: 200, labelX: 228, labelY: 196 },
  { name: 'Eldoret', lat: 0.514, lng: 35.27, x: 300, y: 120, labelX: 318, labelY: 116 },
] as const;

/** `out = slope * input + offset`, least squares over the pairs. */
function fit(pairs: ReadonlyArray<readonly [number, number]>): { slope: number; offset: number } {
  const n = pairs.length;
  const meanIn = pairs.reduce((s, [a]) => s + a, 0) / n;
  const meanOut = pairs.reduce((s, [, b]) => s + b, 0) / n;
  const sxy = pairs.reduce((s, [a, b]) => s + (a - meanIn) * (b - meanOut), 0);
  const sxx = pairs.reduce((s, [a]) => s + (a - meanIn) ** 2, 0);
  const slope = sxy / sxx;
  return { slope, offset: meanOut - slope * meanIn };
}

const east = fit(landmarks.map((l) => [l.lng, l.x] as const));
const north = fit(landmarks.map((l) => [l.lat, l.y] as const));

/** Where a latitude and longitude fall on the board's map. */
export function project(lat: number, lng: number): { x: number; y: number } {
  return { x: east.slope * lng + east.offset, y: north.slope * lat + north.offset };
}

/**
 * One circle of a mark: radius in map units, and its opacity.
 *
 * The board draws a mark as one, two or three concentric circles, more of them
 * the larger it is: 66/38/16 at .28/.4/.75, then pairs whose inner circle is
 * about 0.42 of the outer at about .35/.65, then a lone 22 at .3. The outer
 * radius here is area to count, 66 at the largest mark on screen and never
 * below the board's smallest circle, 11. D12 has the counting.
 */
export type Ring = { r: number; opacity: number };

const largest = 66;
const smallest = 11;

export function ringsFor(count: number, max: number): Ring[] {
  const r = Math.max(smallest, largest * Math.sqrt(max > 0 ? count / max : 0));
  if (r >= 60) {
    return [
      { r, opacity: 0.28 },
      { r: (r * 38) / 66, opacity: 0.4 },
      { r: (r * 16) / 66, opacity: 0.75 },
    ];
  }
  if (r >= 24) {
    return [
      { r, opacity: 0.35 },
      { r: r * 0.42, opacity: 0.65 },
    ];
  }
  return [{ r, opacity: 0.3 }];
}

/** The pills the board draws. Unknown has no pill of its own and shows under All only, D12. */
export type VerdictFilter = 'all' | 'genuine' | 'suspect';

const shown: Record<VerdictFilter, ScanVerdict[]> = {
  all: ['genuine', 'suspect', 'unknown'],
  genuine: ['genuine'],
  suspect: ['suspect'],
};

export interface Mark {
  verdict: ScanVerdict;
  x: number;
  y: number;
  count: number;
  rings: Ring[];
}

/**
 * The marks to draw, in paint order: genuine under suspect under unknown, and
 * within a verdict the largest first, so a small cluster is never hidden under
 * a large one.
 */
export function marksFor(cells: HeatmapCell[], filter: VerdictFilter): Mark[] {
  const verdicts = shown[filter];
  const raw = verdicts.flatMap((verdict) =>
    cells
      .map((cell) => ({ verdict, cell, count: cell.counts[verdict] ?? 0 }))
      .filter((m) => m.count > 0)
      .sort((a, b) => b.count - a.count),
  );
  const max = raw.reduce((m, { count }) => Math.max(m, count), 0);
  return raw.map(({ verdict, cell, count }) => ({
    verdict,
    count,
    ...project(cell.lat, cell.lng),
    rings: ringsFor(count, max),
  }));
}

export interface Hotspot {
  /** The cell's coordinates at the response's precision: no route names a place, D12. */
  place: string;
  verdict: 'unknown' | 'suspect';
  count: number;
}

/**
 * The board's three Hotspots rows: unknown before suspect, as it draws them,
 * then the larger count first.
 */
export function hotspots(heatmap: Heatmap, limit = 3): Hotspot[] {
  const at = (cell: HeatmapCell) =>
    `${cell.lat.toFixed(heatmap.precision)}, ${cell.lng.toFixed(heatmap.precision)}`;
  const of = (verdict: Hotspot['verdict']) =>
    heatmap.cells
      .filter((cell) => (cell.counts[verdict] ?? 0) > 0)
      .map((cell) => ({ place: at(cell), verdict, count: cell.counts[verdict]! }))
      .sort((a, b) => b.count - a.count);
  return [...of('unknown'), ...of('suspect')].slice(0, limit);
}

/**
 * The Verdict split, from the summary rather than the cells: the heat map
 * counts located scans only, and the split is of every scan, D12. `null` when
 * the window has no scans, since a share of nothing is not drawn.
 */
export function verdictSplit(summary: ScanSummary): Record<ScanVerdict, number> | null {
  const total = summary.totalScans;
  if (total <= 0) return null;
  const share = (n: number) => Math.round((n / total) * 1000) / 10;
  return {
    genuine: share(summary.byVerdict.genuine),
    suspect: share(summary.byVerdict.suspect),
    unknown: share(summary.byVerdict.unknown),
  };
}
